import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { Prohibit, Warning } from "@phosphor-icons/react";
import cx from "classnames";
import {
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { PinToneContext } from "./pinToneContext";
import {
  RejectedPinLabelContext,
  rejectedPinKind,
} from "./rejectedPinLabelContext";
import { RejectedPinMark } from "./RejectedPinMark";
import {
  type Rotation,
  clampUnit,
  rotateDelta,
  rotatePoint,
  toCssPercent,
} from "./rotationUtils";

import { isPointInCanvasPinHit } from "../../tickets/components/CommentTool/commentPinHitArea";

/** 24px core + 2px padding on each side (`size-6` + `p-0.5`). */
const ASK_PUNTT_PIN_BODY_PX = 28;

const DRAG_THRESHOLD_PX = 3;

interface ImageCommentPinProps {
  comment: BaseTicketComment;
  /** When set, renders a numbered blue circle badge instead of the teardrop pin. */
  displayNumber?: number;
  isActive: boolean;
  /**
   * When true, the pin renders at reduced opacity to let the active pin
   * stand out. Should only be true when *some other* pin is active —
   * never when nothing is selected, otherwise every pin appears dimmed.
   */
  dimmed?: boolean;
  /** White ring when no comment is selected in the sidebar. */
  showIdleBorder?: boolean;
  onPinClick: (comment: BaseTicketComment) => void;
  onDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  containerRef?: RefObject<HTMLDivElement | null>;
  rotation?: Rotation;
}

function applyPinPosition(
  el: HTMLElement | null,
  displayX: number,
  displayY: number,
) {
  if (!el) return;
  el.style.left = toCssPercent(displayX);
  el.style.top = toCssPercent(displayY);
}

export function ImageCommentPin({
  comment,
  displayNumber,
  isActive,
  dimmed = false,
  showIdleBorder = false,
  onPinClick,
  onDragEnd,
  containerRef,
  rotation = 0,
}: ImageCommentPinProps) {
  const [isDragging, setIsDragging] = useState(false);
  // why: only the Ask v2 viewer provides a labeller. Without one a pin draws
  // as it always has, so a v1 pane showing rejected findings behind its
  // toggle keeps its full-color pins.
  const rejectedPinLabel = useContext(RejectedPinLabelContext);
  const rejectedKind = rejectedPinLabel ? rejectedPinKind(comment) : null;
  const rejectedLabel =
    rejectedPinLabel && rejectedKind
      ? rejectedPinLabel(comment, rejectedKind)
      : null;
  const isRejected = rejectedKind != null;
  const pinTone = useContext(PinToneContext)?.(comment) ?? null;
  const [droppedPin, setDroppedPin] = useState<{
    relX: number;
    relY: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  const dragStartRef = useRef<{
    px: number;
    py: number;
    relX: number;
    relY: number;
  } | null>(null);
  // Tracks whether handlePointerUp already called onPinClick so onClick does
  // not double-fire. For real pointer interactions, pointerup → onClick;
  // pointerHandledRef prevents the onClick from calling onPinClick a second
  // time. For dispatchEvent("click") (used by the e2e spec to bypass pointer
  // actionability checks on overlapping pins), pointerup never fires, so the
  // ref stays false and onClick calls onPinClick correctly.
  const pointerHandledRef = useRef(false);

  const relX = comment.rel_x;
  const relY = comment.rel_y;

  useEffect(() => {
    setDroppedPin(null);
  }, [comment._id]);

  useEffect(() => {
    if (
      droppedPin != null &&
      relX != null &&
      relY != null &&
      relX === droppedPin.relX &&
      relY === droppedPin.relY
    ) {
      setDroppedPin(null);
    }
  }, [relX, relY, droppedPin]);

  // onClick fires after pointerUp for real interactions (pointerHandledRef
  // prevents double-calling onPinClick) AND for dispatchEvent("click") which
  // the e2e spec uses to bypass pointer actionability checks on overlapping
  // pins — in that case pointerUp never fired, so the flag is false and we
  // call onPinClick here.
  const handleClick = useCallback(() => {
    if (pointerHandledRef.current) {
      pointerHandledRef.current = false;
      return;
    }
    onPinClick(comment);
  }, [comment, onPinClick]);

  // Natural-space position. After drop, hold committed coords locally until
  // parent props catch up so the pin does not flash back for one frame.
  const baseRelX = droppedPin?.relX ?? relX;
  const baseRelY = droppedPin?.relY ?? relY;
  // why clamped: christo stores the pin warhol sends unchecked, so a value
  // past the page edge would otherwise draw the pin off the artwork.
  const rotatedDisplay =
    baseRelX != null && baseRelY != null
      ? rotatePoint(
          { x: clampUnit(baseRelX), y: clampUnit(baseRelY) },
          rotation,
        )
      : null;
  const displayX = rotatedDisplay?.x ?? 0;
  const displayY = rotatedDisplay?.y ?? 0;

  // Imperative drag updates `style.left/top` outside React. When dragStart is
  // cleared (cancel / unexpected race) without a displayX/isDragging change,
  // a deps-gated effect would not re-run and the DOM would stay mid-drag.
  // Run every commit; skip only while a gesture owns the position.
  useLayoutEffect(() => {
    if (relX == null || relY == null) return;
    if (dragStartRef.current != null) return;
    applyPinPosition(buttonRef.current, displayX, displayY);
  });

  if (relX == null || relY == null) return null;

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStartRef.current = { px: e.clientX, py: e.clientY, relX, relY };
    dragOffsetRef.current = null;
    pointerHandledRef.current = false;
    setIsDragging(false);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStartRef.current || !containerRef?.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const displayedDx = (e.clientX - dragStartRef.current.px) / rect.width;
    const displayedDy = (e.clientY - dragStartRef.current.py) / rect.height;
    // Rect dimensions are the rotated (displayed) frame. Transform the drag
    // delta back into image-natural space so the stored rel_x/rel_y stay in
    // natural coords regardless of how the image is displayed.
    const natural = rotateDelta({ dx: displayedDx, dy: displayedDy }, rotation);
    dragOffsetRef.current = natural;
    // Clamped like the drop below, so the preview never leaves the artwork.
    const naturalX = clampUnit(dragStartRef.current.relX + natural.dx);
    const naturalY = clampUnit(dragStartRef.current.relY + natural.dy);
    const rotated = rotatePoint({ x: naturalX, y: naturalY }, rotation);
    // DOM write only — avoid React re-renders on every pointermove.
    applyPinPosition(buttonRef.current, rotated.x, rotated.y);
    if (!isDragging) setIsDragging(true);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStartRef.current) return;
    const totalPx =
      Math.abs(e.clientX - dragStartRef.current.px) +
      Math.abs(e.clientY - dragStartRef.current.py);

    if (totalPx > DRAG_THRESHOLD_PX && onDragEnd && containerRef?.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const displayedDx = (e.clientX - dragStartRef.current.px) / rect.width;
      const displayedDy = (e.clientY - dragStartRef.current.py) / rect.height;
      const natural = rotateDelta(
        { dx: displayedDx, dy: displayedDy },
        rotation,
      );
      const newRelX = Math.max(
        0,
        Math.min(1, dragStartRef.current.relX + natural.dx),
      );
      const newRelY = Math.max(
        0,
        Math.min(1, dragStartRef.current.relY + natural.dy),
      );
      setDroppedPin({ relX: newRelX, relY: newRelY });
      onDragEnd(comment, newRelX, newRelY);
      // Suppress the click event that follows a drag so selection is not toggled.
      pointerHandledRef.current = true;
    } else {
      // Mark as handled so onClick (which fires after pointerUp for real
      // interactions) doesn't call onPinClick a second time.
      pointerHandledRef.current = true;
      onPinClick(comment);
      // Snap back to the committed position if the drag was below threshold.
      applyPinPosition(buttonRef.current, displayX, displayY);
    }
    dragStartRef.current = null;
    dragOffsetRef.current = null;
    setIsDragging(false);
  };

  const handlePointerCancel = () => {
    pointerHandledRef.current = false;
    dragStartRef.current = null;
    dragOffsetRef.current = null;
    applyPinPosition(buttonRef.current, displayX, displayY);
    setIsDragging(false);
  };

  if (displayNumber != null) {
    return (
      <button
        ref={buttonRef}
        type="button"
        data-testid="comment-pin"
        data-comment-id={comment._id}
        data-pin-variant="numbered"
        data-pin-tone={pinTone ?? undefined}
        data-rejected={rejectedKind === "here" ? "true" : undefined}
        data-rejected-before={rejectedKind === "before" ? "true" : undefined}
        aria-label={rejectedLabel ?? undefined}
        onPointerDown={(e) => {
          handlePointerDown(e);
        }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onClick={handleClick}
        className="absolute z-20 cursor-grab touch-none border-none bg-transparent p-0"
        style={{
          left: toCssPercent(displayX),
          top: toCssPercent(displayY),
          transform: "translate(-50%, -50%)",
          cursor: isDragging ? "grabbing" : "grab",
        }}
        title={comment.description?.slice(0, 80)}
      >
        <span
          data-testid="comment-pin-number"
          className={cx(
            "relative flex size-7 items-center justify-center rounded-full text-[12px] font-semibold shadow-md transition-all duration-150",
            // why: the two sides of a comparison differ by shape and lightness
            // (a light ring for what was removed, a solid dark pin for what
            // came), so they read apart without telling red from green.
            isRejected
              ? "bg-puntt-neutral-gray-9 text-base-white"
              : pinTone === "before"
                ? "border-2 border-puntt-gold-10 bg-base-white text-puntt-gold-11"
                : pinTone === "after"
                  ? "bg-puntt-blue-12 text-base-white"
                  : "bg-puntt-accent-9 text-base-white",
            isActive &&
              cx(
                "scale-110 ring-2 ring-offset-1",
                isRejected
                  ? "ring-puntt-neutral-gray-8"
                  : pinTone === "before"
                    ? "ring-puntt-gold-10/60"
                    : pinTone === "after"
                      ? "ring-puntt-blue-12/60"
                      : "ring-puntt-accent-9/60",
              ),
            showIdleBorder &&
              !isActive &&
              !dimmed &&
              "ring-2 ring-base-white ring-offset-0",
            (dimmed || isRejected) && !isActive && "opacity-60",
            isRejected && !isActive && "hover:opacity-100",
          )}
        >
          {displayNumber}
          {rejectedKind ? (
            <RejectedPinMark commentId={comment._id} kind={rejectedKind} />
          ) : null}
        </span>
      </button>
    );
  }

  const Icon = comment.isRequired ? Prohibit : Warning;
  // The teardrop shell wraps the inner circle, so its color shows as an
  // outline. Both layers use the same severity-driven background (red for
  // required, gold for suggested) to keep the pin visually consistent.
  const severityBg = isRejected
    ? "bg-puntt-neutral-gray-9"
    : comment.isRequired
      ? "bg-puntt-red-9"
      : "bg-puntt-gold-10";
  // Active pins keep their severity hue — testers found the prior blue accent
  // jarring during QA. The focused state is conveyed via a same-severity ring
  // plus scale-up of the active pin; sibling pins are dimmed via the parent's
  // `dimmed` prop only when *another* pin is active, so pins do not appear
  // permanently faded when no comment is selected.
  const severityRing = isRejected
    ? "ring-puntt-neutral-gray-8"
    : comment.isRequired
      ? "ring-puntt-red-9/60"
      : "ring-puntt-gold-10/60";

  const isInsidePinHit = (
    clientX: number,
    clientY: number,
    button: HTMLElement,
  ) => {
    const rect = button.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    // Tip anchors at the button top-left; polygon math uses tip at (0, bodySize).
    return isPointInCanvasPinHit(
      localX,
      localY + ASK_PUNTT_PIN_BODY_PX,
      ASK_PUNTT_PIN_BODY_PX,
    );
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      data-testid="comment-pin"
      data-comment-id={comment._id}
      data-required={comment.isRequired ?? false}
      data-rejected={rejectedKind === "here" ? "true" : undefined}
      data-rejected-before={rejectedKind === "before" ? "true" : undefined}
      aria-label={rejectedLabel ?? undefined}
      onPointerDown={(e) => {
        if (!isInsidePinHit(e.clientX, e.clientY, e.currentTarget)) return;
        handlePointerDown(e);
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onClick={handleClick}
      className="absolute z-20 cursor-grab touch-none border-none bg-transparent p-0"
      style={{
        left: toCssPercent(displayX),
        top: toCssPercent(displayY),
        cursor: isDragging ? "grabbing" : "grab",
      }}
      title={comment.description?.slice(0, 80)}
    >
      <div
        data-testid="comment-pin-shell"
        className={cx(
          "rounded-t-2xl rounded-br-2xl p-0.5 shadow-md transition-all duration-150",
          severityBg,
          isActive && cx("ring-2 ring-offset-1", severityRing),
          (dimmed || isRejected) && !isActive && "opacity-60",
          isRejected && !isActive && "hover:opacity-100",
        )}
        style={{
          transform: `translateY(-100%) rotate(-45deg) ${
            isActive ? "scale(1.35)" : "scale(1)"
          }`,
          transformOrigin: "0% 100%",
        }}
      >
        <div
          data-testid="comment-pin-core"
          className={cx(
            "relative flex size-6 items-center justify-center rounded-full",
            severityBg,
          )}
          style={{ transform: "rotate(45deg)" }}
        >
          <Icon size={14} weight="fill" className="text-base-white" />
          {rejectedKind ? (
            <RejectedPinMark commentId={comment._id} kind={rejectedKind} />
          ) : null}
        </div>
      </div>
    </button>
  );
}
